import { useState, useEffect } from 'react';

const ProblemStatement = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('problem-statement');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight * 0.75) {
          setIsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="problem-statement" className="py-12 md:py-16 bg-slate-50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className={`text-3xl md:text-4xl font-bold text-abbella-ebony mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            Traditional Home Care Is Broken
          </h2>
          <p className={`text-lg text-slate-600 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            For too long, seniors and families have settled for a home care model that doesn't truly serve their needs.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Comparison Table */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="bg-red-50 p-5">
                <h3 className="text-xl font-bold text-red-700 mb-1">Traditional Home Care</h3>
                <p className="text-sm text-red-600">The frustrating reality</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-red-600 text-xs">✕</span>
                  </div>
                  <p><strong>Unreliable Staffing:</strong> Frequent no-shows and last-minute cancellations leaving families scrambling</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-red-600 text-xs">✕</span>
                  </div>
                  <p><strong>Rigid Scheduling:</strong> Inflexible appointment times that don't adapt to your changing needs</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-red-600 text-xs">✕</span>
                  </div>
                  <p><strong>Hourly Minimums:</strong> Forced to pay for 3-4 hour blocks when you might only need 30 minutes of help</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-red-600 text-xs">✕</span>
                  </div>
                  <p><strong>Rotating Caregivers:</strong> New faces constantly appearing at your door, creating anxiety and inconsistent care</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* FlowCare Approach */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="bg-abbella-aqua/10 p-5">
                <h3 className="text-xl font-bold text-abbella-aqua mb-1">The FlowCare Approach</h3>
                <p className="text-sm text-abbella-aqua/80">Peace of mind, guaranteed</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <p><strong>Guaranteed Reliability:</strong> Our innovative CareBase model ensures we always show up when promised</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <p><strong>Flexible Scheduling:</strong> Care appointments that adapt to your life, not the other way around</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <p><strong>No Minimums:</strong> Pay only for the exact time you need, whether it's 15 minutes or several hours</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-green-600 text-xs">✓</span>
                  </div>
                  <p><strong>Dedicated Care Teams:</strong> Build relationships with a small, consistent team who truly knows you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Image Section */}
        <div className="mt-16 grid md:grid-cols-2 gap-8 items-center">
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            <img 
              src="/lovable-uploads/ae76a788-25d6-44bd-8aad-0c441207164f.png" 
              alt="Compassionate home care interaction" 
              className="rounded-xl shadow-lg w-full h-auto object-cover"
              style={{ maxHeight: "400px" }}
            />
          </div>
          <div className={`transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-8'}`}>
            <h3 className="text-2xl font-bold text-abbella-ebony mb-4">A New Era in Home Care</h3>
            <p className="text-lg text-slate-600 mb-6">
              FlowCare isn't just another home care agency. We've reimagined what's possible when technology, community, and compassionate care come together.
            </p>
            <p className="text-lg text-slate-600">
              Our revolutionary approach means you get professional care exactly when you need it, with no compromises on quality or reliability.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemStatement;
